/**
 * GuestRsvpPage — iter197.
 *
 * Public route at `/guest/rsvp/:token`. Anyone with the magic link can see
 * the session venue/time and one-tap join or leave the headcount. No
 * SplitEasy account required. Token-only auth (validated server-side per
 * request).
 *
 * Token TTL: 48h (admin can mint with a short-lived window).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, Clock, MapPin, Users, CheckCircle2, XCircle, Loader2,
  ShieldAlert, ExternalLink, Coins, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BASE = process.env.REACT_APP_BACKEND_URL;
const publicAxios = axios.create({ baseURL: `${BASE}/api` });

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return d; }
}

function fmtTime(t) {
  if (!t) return '';
  return t.slice(0, 5);
}

export default function GuestRsvpPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await publicAxios.get(`/guest/rsvp/${token}`);
      setData(r.data);
      const presetName = r.data?.invitation?.guest_name || '';
      if (presetName) setName(presetName);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Link is invalid or expired');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onJoin = useCallback(async () => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error('Please enter your name');
      return;
    }
    setWorking(true);
    try {
      const r = await publicAxios.post(`/guest/rsvp/${token}/join`, { guest_name: cleanName });
      toast.success('You\'re in! See you at the session.');
      setData((prev) => prev ? { ...prev, already_joined: true, session: { ...prev.session, headcount: r.data?.headcount ?? prev.session.headcount } } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not join');
    } finally {
      setWorking(false);
    }
  }, [name, token]);

  const onLeave = useCallback(async () => {
    if (!window.confirm('Remove yourself from this session?')) return;
    setWorking(true);
    try {
      await publicAxios.post(`/guest/rsvp/${token}/leave`);
      toast.success('Removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove');
    } finally {
      setWorking(false);
    }
  }, [token, load]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">Loading invitation…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <ShieldAlert className="w-10 h-10 text-amber-500 mb-3" />
        <h1 className="font-display text-xl font-bold mb-1">Link unavailable</h1>
        <p className="text-sm text-muted-foreground max-w-xs" data-testid="guest-rsvp-error">{error}</p>
        <Button onClick={() => navigate('/welcome')} variant="outline" className="mt-6">Back to home</Button>
      </div>
    );
  }

  if (!data) return null;

  const { group, session, invitation, already_joined } = data;
  const isClosed = session.rsvp_locked || ['cancelled', 'completed'].includes(session.status);

  return (
    <div className="min-h-screen pb-32 bg-gradient-to-b from-background to-muted/30" data-testid="guest-rsvp-page">
      <main className="max-w-md mx-auto px-4 py-8 space-y-5">
        {/* Brand strip */}
        <div className="text-center">
          <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
            <Sparkles className="w-3 h-3" /> SplitEasy · Guest invitation
          </p>
        </div>

        {/* Header */}
        <header className="text-center space-y-2" data-testid="guest-rsvp-header">
          <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
            You&apos;re invited to {session.title}
          </h1>
          <p className="text-sm text-muted-foreground">
            From <span className="font-semibold text-foreground">{invitation.invited_by || 'an admin'}</span> of {group.name}
          </p>
          {invitation.note && (
            <p className="text-xs text-muted-foreground italic max-w-sm mx-auto">
              &ldquo;{invitation.note}&rdquo;
            </p>
          )}
        </header>

        {/* Session card */}
        <section className="rounded-3xl border-2 border-border bg-card p-5 space-y-3" data-testid="guest-rsvp-session-card">
          <Row Icon={Calendar} label="When">
            {fmtDate(session.date)} · {fmtTime(session.start_time)}
            {session.end_time ? `–${fmtTime(session.end_time)}` : ''}
          </Row>
          {session.venue_name && (
            <Row Icon={MapPin} label="Where">
              {session.venue_map_url ? (
                <a
                  href={session.venue_map_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-2 hover:underline inline-flex items-center gap-1"
                  data-testid="guest-rsvp-venue-link"
                >
                  {session.venue_name} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span>{session.venue_name}</span>
              )}
            </Row>
          )}
          <Row Icon={Users} label="Headcount">
            <span className="tabular-nums" data-testid="guest-rsvp-headcount">
              {session.headcount}{session.min_players ? ` / ${session.min_players} min` : ''}
            </span>
          </Row>
          {session.session_fee > 0 && (
            <Row Icon={Coins} label="Session fee">
              <span className="tabular-nums">₹{Math.round(session.session_fee).toLocaleString()}</span>
              <span className="ml-1.5 text-xs text-muted-foreground">cash to the host</span>
            </Row>
          )}
        </section>

        {/* Status banner */}
        {isClosed ? (
          <div className="rounded-2xl border-2 border-red-500/40 bg-red-500/10 p-4 text-center text-sm font-semibold text-red-600 dark:text-red-300" data-testid="guest-rsvp-closed">
            RSVP is closed for this session.
          </div>
        ) : already_joined ? (
          <section className="rounded-3xl border-2 border-emerald-500/40 bg-emerald-500/10 p-5 text-center space-y-3" data-testid="guest-rsvp-joined">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400 mx-auto" />
            <p className="font-display text-base font-bold text-emerald-700 dark:text-emerald-300">
              You&apos;re on the headcount.
            </p>
            <p className="text-xs text-muted-foreground">
              Save this link — you can come back here to leave the session if your plans change.
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={onLeave}
              disabled={working}
              className="text-red-600 hover:text-red-700 gap-1.5"
              data-testid="guest-rsvp-leave-btn"
            >
              {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
              Remove me
            </Button>
          </section>
        ) : (
          <section className="rounded-3xl border-2 border-border bg-card p-5 space-y-4" data-testid="guest-rsvp-join-card">
            <div>
              <Label htmlFor="guest-name" className="text-xs uppercase tracking-wider font-semibold">Your name</Label>
              <Input
                id="guest-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aman Patel"
                className="mt-1.5 text-base"
                autoFocus
                data-testid="guest-rsvp-name-input"
              />
            </div>
            <Button
              className="w-full gap-2"
              size="lg"
              onClick={onJoin}
              disabled={working || !name.trim()}
              data-testid="guest-rsvp-join-btn"
            >
              {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {working ? 'Joining…' : "I'm in — count me"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              No account needed. Your name will show on the host&apos;s session roster.
            </p>
          </section>
        )}

        <p className="text-xs text-center text-muted-foreground pt-3" data-testid="guest-rsvp-expiry">
          This link expires {new Date(data.expires_at).toLocaleString()}.
        </p>

        <div className="text-center pt-2">
          <a
            href="/welcome"
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            Learn about SplitEasy
          </a>
        </div>
      </main>
    </div>
  );
}

function Row({ Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold mt-0.5 break-words">{children}</p>
      </div>
    </div>
  );
}
